import { TournamentType } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { Button } from '@/src/presentation/components';
import { FontAwesome5, Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { KeyboardAvoidingView, Platform, ScrollView, Switch, Text, TouchableOpacity, View, TextInput } from 'react-native';
import { createStyles } from './CreateTournamentScreen.styles';
import { TournamentTypeCard } from './TournamentTypeCard';

export interface CreateTournamentScreenProps {
  name: string;
  setName: (value: string) => void;
  description: string;
  setDescription: (value: string) => void;
  selectedType: TournamentType;
  setSelectedType: (value: TournamentType) => void;
  maxParticipants: number | undefined;
  setMaxParticipants: (value: number | undefined) => void;
  isPublic: boolean;
  setIsPublic: (value: boolean) => void;
  creating: boolean;
  handleCreate: () => void;
  handleCancel: () => void;
  allowedTournamentTypes?: TournamentType[];
  maxParticipantsPerPlan?: number;
  colors: {
    background: string;
    card: string;
    border: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
    primary: string;
    primaryText: string;
    error: string;
  };
}

const PARTICIPANT_LIMITS = [
  { value: 4, label: '4' },
  { value: 8, label: '8' },
  { value: 16, label: '16' },
  { value: 32, label: '32' },
  { value: 64, label: '64' },
  { value: 512, label: '512' },
];

export const CreateTournamentScreen = ({
  name,
  setName,
  description,
  setDescription,
  selectedType,
  setSelectedType,
  isPublic,
  setIsPublic,
  maxParticipants,
  setMaxParticipants,
  creating,
  handleCreate,
  handleCancel,
  allowedTournamentTypes,
  maxParticipantsPerPlan,
  colors,
}: CreateTournamentScreenProps) => {
  const styles = createStyles();
  const { t } = useTranslation();

  const isLocked = (type: TournamentType) =>
    allowedTournamentTypes !== undefined && !allowedTournamentTypes.includes(type);

  const isParticipantOptionLocked = (optionValue: number): boolean => {
    if (maxParticipantsPerPlan === undefined) return false;
    return optionValue > maxParticipantsPerPlan;
  };

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={[styles.container, { backgroundColor: colors.background }]}
    >
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.section}>
          <Text style={[styles.label, { color: colors.text }]}>{t('tournaments.tournamentName')} {t('tournaments.required')}</Text>
          <TextInput
            style={[
              styles.input,
              { backgroundColor: colors.card, borderColor: colors.border, color: colors.text },
            ]}
            placeholder={t('tournaments.enterName')}
            placeholderTextColor={colors.textTertiary}
            value={name}
            onChangeText={setName}
            autoFocus
          />
        </View>

        <View style={styles.section}>
          <Text style={[styles.label, { color: colors.text }]}>{t('tournaments.descriptionOptional')}</Text>
          <TextInput
            style={[
              styles.input,
              styles.textArea,
              { backgroundColor: colors.card, borderColor: colors.border, color: colors.text },
            ]}
            placeholder={t('tournaments.enterDescription')}
            placeholderTextColor={colors.textTertiary}
            value={description}
            onChangeText={setDescription}
            multiline
            numberOfLines={3}
            maxLength={500}
          />
          <Text style={{ color: colors.textTertiary, fontSize: 12, textAlign: 'right', marginTop: 4 }}>
            {description.length}/500
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={[styles.label, { color: colors.text }]}>{t('tournaments.tournamentType')} {t('tournaments.required')}</Text>

          <TournamentTypeCard
            type="single_elimination"
            selectedType={selectedType}
            onPress={() => setSelectedType('single_elimination')}
            title={t('tournaments.singleElimination')}
            description={t('tournaments.singleEliminationDesc')}
            icon={<FontAwesome5 name="trophy" size={24} color={colors.text} style={{ marginRight: 12 }} />}
            locked={isLocked('single_elimination')}
            colors={colors}
            styles={styles}
          />

          <TournamentTypeCard
            type="double_elimination"
            selectedType={selectedType}
            onPress={() => setSelectedType('double_elimination')}
            title={t('tournaments.doubleElimination')}
            description={t('tournaments.doubleEliminationDesc')}
            icon={<MaterialCommunityIcons name="tournament" size={28} color={colors.text} style={{ marginRight: 12 }} />}
            locked={isLocked('double_elimination')}
            colors={colors}
            styles={styles}
          />

          <TournamentTypeCard
            type="round_robin"
            selectedType={selectedType}
            onPress={() => setSelectedType('round_robin')}
            title={t('tournaments.roundRobin')}
            description={t('tournaments.roundRobinDesc')}
            icon={<MaterialCommunityIcons name="sync-circle" size={28} color={colors.text} style={{ marginRight: 12 }} />}
            locked={isLocked('round_robin')}
            colors={colors}
            styles={styles}
          />

          <TournamentTypeCard
            type="swiss"
            selectedType={selectedType}
            onPress={() => setSelectedType('swiss')}
            title={t('tournaments.swiss')}
            description={t('tournaments.swissDesc')}
            icon={<MaterialCommunityIcons name="grid" size={28} color={colors.text} style={{ marginRight: 12 }} />}
            locked={isLocked('swiss')}
            colors={colors}
            styles={styles}
          />

          <TournamentTypeCard
            type="triple_elimination"
            selectedType={selectedType}
            onPress={() => setSelectedType('triple_elimination')}
            title={t('tournaments.tripleElimination')}
            description={t('tournaments.tripleEliminationDesc')}
            icon={<MaterialCommunityIcons name="numeric-3-circle" size={28} color={colors.text} style={{ marginRight: 12 }} />}
            locked={isLocked('triple_elimination')}
            colors={colors}
            styles={styles}
          />

          <TournamentTypeCard
            type="king_of_the_hill"
            selectedType={selectedType}
            onPress={() => setSelectedType('king_of_the_hill')}
            title={t('tournaments.kingOfTheHill')}
            description={t('tournaments.kingOfTheHillDesc')}
            icon={<MaterialCommunityIcons name="crown" size={28} color={colors.text} style={{ marginRight: 12 }} />}
            locked={isLocked('king_of_the_hill')}
            colors={colors}
            styles={styles}
          />
        </View>

        <View style={styles.section}>
          <Text style={[styles.label, { color: colors.text }]}>{t('tournaments.maxParticipants')}</Text>
          {selectedType === 'triple_elimination' ? (
            <View style={[styles.fixedParticipantsNotice, { backgroundColor: colors.card, borderColor: colors.border }]}>
              <Ionicons name="information-circle" size={18} color={colors.primary} style={{ marginRight: 8, flexShrink: 0 }} />
              <Text style={[styles.fixedParticipantsText, { color: colors.textSecondary }]}>
                {t('tournaments.tripleEliminationParticipantsFixed')}
              </Text>
            </View>
          ) : (
            <>
              <Text style={[styles.labelHint, { color: colors.textSecondary }]}>
                {t('tournaments.maxParticipantsHint')}
              </Text>
              <View style={styles.limitOptions}>
                {PARTICIPANT_LIMITS.map((option) => {
                  const locked = isParticipantOptionLocked(option.value);
                  const selected = maxParticipants === option.value;
                  return (
                    <TouchableOpacity
                      key={option.label}
                      disabled={locked}
                      style={[
                        styles.limitOption,
                        {
                          backgroundColor: selected ? colors.primary : colors.card,
                          borderColor: selected ? colors.primary : colors.border,
                          opacity: locked ? 0.6 : 1,
                        },
                      ]}
                      onPress={() => setMaxParticipants(option.value)}
                    >
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                        <Text
                          style={[
                            styles.limitOptionText,
                            { color: locked ? colors.textTertiary : selected ? colors.primaryText : colors.text },
                          ]}
                        >
                          {option.label}
                        </Text>
                        {locked && (
                          <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#FFD700', paddingHorizontal: 4, paddingVertical: 1, borderRadius: 4 }}>
                            <Ionicons name="lock-closed" size={8} color="#8B4513" />
                            <Text style={{ fontSize: 8, fontWeight: 'bold', color: '#8B4513', marginLeft: 1 }}>PRO</Text>
                          </View>
                        )}
                      </View>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </>
          )}
        </View>

        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 12, paddingHorizontal: 4, marginBottom: 8 }}>
          <View style={{ flex: 1 }}>
            <Text style={[{ fontSize: 16, fontWeight: '500', color: colors.text }]}>{t('tournaments.publicTournament')}</Text>
            <Text style={[{ fontSize: 13, color: colors.textSecondary, marginTop: 2 }]}>{t('tournaments.publicTournamentHint')}</Text>
          </View>
          <Switch value={isPublic} onValueChange={setIsPublic} trackColor={{ false: colors.border, true: colors.primary }} thumbColor="#fff" />
        </View>

        <Button title={creating ? t('tournaments.creating') : t('common.create')} onPress={handleCreate} disabled={creating} loading={creating} colors={colors} />

        <TouchableOpacity style={styles.cancelBtn} onPress={handleCancel} disabled={creating}>
          <Text style={[styles.cancelBtnText, { color: colors.primary }]}>{t('common.cancel')}</Text>
        </TouchableOpacity>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};
